import { calendarDisplayLabel } from '@/utils/calendar/display'
import type { CalendarSidebarItem } from '@/types/calendar/sidebar'
import type { CalendarEventTypeEnum, CustomerCalendar } from '@/types/generated'
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

type SidebarFilterLabelMap = Record<CalendarEventTypeEnum, string>

const filterKey = (calendarUuid: string, type: CalendarEventTypeEnum): string =>
  `${calendarUuid}:${type}`

/**
 * Sidebar list model: focused calendar rows + event-type filters driven by loaded events.
 */
export function useCalendarSidebar(
  visibleCalendars: Ref<CustomerCalendar[]>,
  selectedCalendarUuid: Ref<string | null>,
  availableTypesByCalendar: ComputedRef<Record<string, CalendarEventTypeEnum[]>>,
  filterLabels: Ref<SidebarFilterLabelMap>
) {
  const expandedCalendarUuids = ref<Record<string, boolean>>({})
  const filterChecks = ref<Record<string, boolean>>({})
  const isChecked = (calendarUuid: string, type: CalendarEventTypeEnum): boolean =>
    filterChecks.value[filterKey(calendarUuid, type)] !== false

  const sidebarItems = computed<CalendarSidebarItem[]>(() =>
    visibleCalendars.value
      .filter((cal) => cal.uuid)
      .map((cal) => ({
        uuid: cal.uuid!,
        label: calendarDisplayLabel(cal),
        filters: (availableTypesByCalendar.value[cal.uuid!] ?? []).map((type) => ({
          id: filterKey(cal.uuid!, type),
          type,
          label: filterLabels.value[type],
          checked: isChecked(cal.uuid!, type)
        }))
      }))
  )

  const activeFilterTypes = computed<Set<CalendarEventTypeEnum>>(() => {
    const activeTypes = new Set<CalendarEventTypeEnum>()
    const selectedUuid = selectedCalendarUuid.value

    if (!selectedUuid) {
      return activeTypes
    }

    for (const type of availableTypesByCalendar.value[selectedUuid] ?? []) {
      if (isChecked(selectedUuid, type)) {
        activeTypes.add(type)
      }
    }

    return activeTypes
  })

  const seedMissingFilterChecks = (): void => {
    const nextChecks = { ...filterChecks.value }

    for (const calendar of visibleCalendars.value) {
      if (!calendar.uuid) {
        continue
      }

      for (const type of availableTypesByCalendar.value[calendar.uuid] ?? []) {
        const key = filterKey(calendar.uuid, type)
        if (nextChecks[key] === undefined) {
          nextChecks[key] = true
        }
      }
    }

    filterChecks.value = nextChecks
  }

  watch(
    () => sidebarItems.value.map((i) => i.uuid),
    (uuids) => {
      const nextExpandedState: Record<string, boolean> = {}

      for (const uuid of uuids) {
        nextExpandedState[uuid] =
          expandedCalendarUuids.value[uuid] ?? uuid === selectedCalendarUuid.value
      }

      expandedCalendarUuids.value = nextExpandedState
    },
    { immediate: true }
  )

  watch(
    [selectedCalendarUuid, availableTypesByCalendar],
    () => {
      seedMissingFilterChecks()
    },
    { immediate: true }
  )

  watch(selectedCalendarUuid, (uuid) => {
    if (uuid) {
      expandedCalendarUuids.value = {
        ...expandedCalendarUuids.value,
        [uuid]: true
      }
    }
  })

  const toggleFiltersExpanded = (uuid: string): void => {
    expandedCalendarUuids.value[uuid] = !expandedCalendarUuids.value[uuid]
  }

  const ensureFiltersExpanded = (uuid: string): void => {
    expandedCalendarUuids.value[uuid] = true
  }

  const toggleFilter = (calendarUuid: string, type: CalendarEventTypeEnum): void => {
    const availableTypes = availableTypesByCalendar.value[calendarUuid] ?? []
    const key = filterKey(calendarUuid, type)
    const nextChecked = !isChecked(calendarUuid, type)

    if (selectedCalendarUuid.value !== calendarUuid) {
      const hasExistingState = availableTypes.some(
        (availableType) => filterChecks.value[filterKey(calendarUuid, availableType)] !== undefined
      )

      const nextChecks = { ...filterChecks.value }
      if (!hasExistingState) {
        for (const availableType of availableTypes) {
          nextChecks[filterKey(calendarUuid, availableType)] = true
        }
      }

      nextChecks[key] = nextChecked
      filterChecks.value = nextChecks
      selectedCalendarUuid.value = calendarUuid
      return
    }

    filterChecks.value = {
      ...filterChecks.value,
      [key]: nextChecked
    }
  }

  const isFilterChecked = (calendarUuid: string, type: CalendarEventTypeEnum): boolean =>
    isChecked(calendarUuid, type)

  const setFilterChecked = (
    calendarUuid: string,
    type: CalendarEventTypeEnum,
    checked: boolean
  ): void => {
    filterChecks.value = {
      ...filterChecks.value,
      [filterKey(calendarUuid, type)]: checked
    }
  }

  return {
    sidebarItems,
    expandedCalendarUuids,
    activeFilterTypes,
    toggleFiltersExpanded,
    ensureFiltersExpanded,
    toggleFilter,
    isFilterChecked,
    setFilterChecked
  }
}
